/**
 * What a coach's billing state means for what they can do.
 *
 * The rule, decided with Coleman: a failed payment should be loud and
 * survivable, not silent and then fatal. Today it is the opposite. Stripe
 * retries for about two weeks, the coach gets one email, and then access
 * disappears with no in-app warning at any point. So the grace period
 * already exists; what was missing was telling anyone about it.
 *
 *   day 0-6    full access, banner on every page
 *   day 7-13   read-only, banner blocks the save actions
 *   day 14+    Stripe gives up, fires subscription.deleted, access ends
 *
 * Pure on purpose: the same three states are needed by the banner, by the
 * save paths, and by the cron that sends the reminder emails, and a rule
 * that is computed three times is a rule that will disagree with itself.
 */

/** Days of full access after the first failed charge. */
export const GRACE_DAYS = 7;

export const BILLING_OK = "ok";
export const BILLING_PAST_DUE = "past_due";     // failing, still full access
export const BILLING_RESTRICTED = "restricted"; // failing, grace spent, read-only

/**
 * Resolve the state from a profile row.
 *
 * `grace_ends_at` is the authority rather than a day count computed here,
 * because the webhook sets it once from the real failure time. Recomputing
 * from `past_due_since` on every render would let a clock skew between the
 * browser and the server move someone's deadline.
 *
 * A missing `grace_ends_at` on a past-due row resolves to past_due, never
 * restricted: if the webhook half-wrote the row, the safe direction is the
 * one that does not lock a paying coach out.
 */
export function billingState(profile, now = Date.now()) {
  if (!profile || profile.billing_status !== BILLING_PAST_DUE) return BILLING_OK;
  const ends = profile.grace_ends_at ? new Date(profile.grace_ends_at).getTime() : NaN;
  if (!Number.isFinite(ends)) return BILLING_PAST_DUE;
  return now >= ends ? BILLING_RESTRICTED : BILLING_PAST_DUE;
}

/** Everything still readable until Stripe actually cancels. */
export function canRead(profile, now = Date.now()) {
  return billingState(profile, now) !== "never";
}

/**
 * Can they still save, create, upload?
 *
 * This is the only thing the grace period actually takes away. Reading is
 * never blocked: a coach mid-season who loses their drill library over an
 * expired card is a coach who never comes back, and the goal is to get the
 * card fixed, not to punish.
 */
export function canWrite(profile, now = Date.now()) {
  return billingState(profile, now) !== BILLING_RESTRICTED;
}

/** Whole days left of full access. 0 means it runs out today. */
export function graceDaysLeft(profile, now = Date.now()) {
  if (!profile?.grace_ends_at) return null;
  const ms = new Date(profile.grace_ends_at).getTime() - now;
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.ceil(ms / 86400000));
}

/**
 * The line a coach reads in the banner.
 *
 * Specific about the deadline and specific about the fix, because a vague
 * "there was a problem with your payment" is the kind of message people
 * assume is spam. Never says "your subscription has been cancelled", which
 * is both untrue during grace and the exact phrase that makes someone
 * decide they have already lost the thing and stop trying.
 */
export function billingMessage(profile, now = Date.now()) {
  const state = billingState(profile, now);
  if (state === BILLING_OK) return null;
  if (state === BILLING_RESTRICTED) {
    return {
      tone: "urgent",
      title: "Your account is read-only",
      body: "We could not take your last payment, so saving is paused. "
          + "Update your card and everything switches back on straight away.",
      cta: "Update card",
    };
  }
  const left = graceDaysLeft(profile, now);
  return {
    tone: "warn",
    title: "Your last payment did not go through",
    body: left === 0
      ? "Saving is paused at the end of today unless your card is updated."
      : `You have full access for ${left} more ${left === 1 ? "day" : "days"}. `
        + "Updating your card takes about a minute.",
    cta: "Update card",
  };
}
